import { jsx as _jsx } from "react/jsx-runtime";
import { useCallback, useMemo, useRef, useState } from 'react';
import { buildShapeFromDrawing } from './buildShapeFromDrawing';
import { DrawingPreview } from './DrawingPreview';
import { INITIAL_DRAWING_STATE, isShapeDrawingTool, } from './types';
export function useStageDrawing({ currentTool, getStageCoordinates, isPointerInDrawingWorkspace, snapValue, fillColor, strokeColor, strokeWidth, strokeStyle, objectCount, persistShapeObject, addSceneObject, select, }) {
    const [drawingState, setDrawingState] = useState(INITIAL_DRAWING_STATE);
    const drawingStateRef = useRef(INITIAL_DRAWING_STATE);
    const drawingSessionRef = useRef(null);
    const styleRef = useRef({
        snapValue,
        fillColor,
        strokeColor,
        strokeWidth,
        strokeStyle,
        objectCount,
        persistShapeObject,
        addSceneObject,
        select,
        getStageCoordinates,
    });
    styleRef.current = {
        snapValue,
        fillColor,
        strokeColor,
        strokeWidth,
        strokeStyle,
        objectCount,
        persistShapeObject,
        addSceneObject,
        select,
        getStageCoordinates,
    };
    const setDrawing = useCallback((next) => {
        drawingStateRef.current = next;
        setDrawingState(next);
    }, []);
    const endDrawingSession = useCallback(() => {
        const session = drawingSessionRef.current;
        if (!session)
            return;
        session.captureTarget.removeEventListener('pointermove', session.onMove);
        session.captureTarget.removeEventListener('pointerup', session.onUp);
        session.captureTarget.removeEventListener('pointercancel', session.onUp);
        window.removeEventListener('pointermove', session.onMove);
        window.removeEventListener('pointerup', session.onUp);
        window.removeEventListener('pointercancel', session.onUp);
        try {
            if (session.captureTarget.hasPointerCapture(session.pointerId)) {
                session.captureTarget.releasePointerCapture(session.pointerId);
            }
        }
        catch {
            // ignore
        }
        drawingSessionRef.current = null;
    }, []);
    const finalizeDrawing = useCallback((ev) => {
        if (!drawingSessionRef.current)
            return;
        const prev = drawingStateRef.current;
        if (!prev.isDrawing || !prev.tool) {
            endDrawingSession();
            setDrawing(INITIAL_DRAWING_STATE);
            return;
        }
        const finalCoords = styleRef.current.getStageCoordinates(ev);
        const finalState = finalCoords
            ? { ...prev, currentX: finalCoords.x, currentY: finalCoords.y }
            : prev;
        const newObject = buildShapeFromDrawing(finalState, {
            snapValue: styleRef.current.snapValue,
            fillColor: styleRef.current.fillColor,
            strokeColor: styleRef.current.strokeColor,
            strokeWidth: styleRef.current.strokeWidth,
            strokeStyle: styleRef.current.strokeStyle,
            zIndex: styleRef.current.objectCount,
        });
        setDrawing(INITIAL_DRAWING_STATE);
        endDrawingSession();
        if (!newObject)
            return;
        void styleRef.current
            .persistShapeObject(newObject)
            .then((createdObject) => {
            if (!createdObject)
                return;
            styleRef.current.addSceneObject(createdObject);
            styleRef.current.select(createdObject.id);
        })
            .catch((error) => {
            console.error('[useStageDrawing] Failed to persist drawn scene object:', error);
        });
    }, [endDrawingSession, setDrawing]);
    const isShapeDrawingToolSelected = isShapeDrawingTool(currentTool);
    const handleDrawingMouseDown = useCallback((e) => {
        if (e.button !== 0)
            return;
        if (!isShapeDrawingTool(currentTool))
            return;
        if (!isPointerInDrawingWorkspace(e, e.target))
            return;
        if (drawingSessionRef.current)
            return;
        const coords = getStageCoordinates(e);
        if (!coords)
            return;
        e.preventDefault();
        e.stopPropagation();
        const initialState = {
            isDrawing: true,
            startX: coords.x,
            startY: coords.y,
            currentX: coords.x,
            currentY: coords.y,
            tool: currentTool,
        };
        setDrawing(initialState);
        const captureTarget = e.currentTarget;
        try {
            captureTarget.setPointerCapture(e.pointerId);
        }
        catch {
            // window fallback listeners still handle the gesture
        }
        const onMove = (event) => {
            const ev = event;
            if (ev.pointerId !== e.pointerId)
                return;
            const moveCoords = styleRef.current.getStageCoordinates(ev);
            if (!moveCoords)
                return;
            const prev = drawingStateRef.current;
            if (!prev.isDrawing)
                return;
            const next = {
                ...prev,
                currentX: moveCoords.x,
                currentY: moveCoords.y,
            };
            if (prev.tool === 'curve' && prev.controlX == null) {
                const dx = moveCoords.x - prev.startX;
                const dy = moveCoords.y - prev.startY;
                const dist = Math.sqrt(dx * dx + dy * dy);
                if (dist >= 20) {
                    next.controlX = moveCoords.x;
                    next.controlY = moveCoords.y;
                }
            }
            setDrawing(next);
        };
        const onUp = (event) => {
            const ev = event;
            if (ev.pointerId !== e.pointerId)
                return;
            finalizeDrawing(ev);
        };
        drawingSessionRef.current = {
            pointerId: e.pointerId,
            captureTarget,
            onMove,
            onUp,
        };
        captureTarget.addEventListener('pointermove', onMove);
        captureTarget.addEventListener('pointerup', onUp);
        captureTarget.addEventListener('pointercancel', onUp);
        window.addEventListener('pointermove', onMove);
        window.addEventListener('pointerup', onUp);
        window.addEventListener('pointercancel', onUp);
    }, [
        currentTool,
        getStageCoordinates,
        isPointerInDrawingWorkspace,
        finalizeDrawing,
        setDrawing,
    ]);
    const drawingPreview = useMemo(() => (_jsx(DrawingPreview, { state: drawingState, fillColor: fillColor, strokeColor: strokeColor, strokeWidth: strokeWidth, strokeStyle: strokeStyle })), [drawingState, fillColor, strokeColor, strokeWidth, strokeStyle]);
    return {
        drawingState,
        isDrawingActive: drawingState.isDrawing,
        isShapeDrawingToolSelected,
        handleDrawingMouseDown,
        drawingPreview,
    };
}
